import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Table,
  Text,
} from "@mantine/core";
import { IconExternalLink, IconRefresh } from "@tabler/icons-react";
import { Consultation, id_util, tu } from "beitary-shared";
import { BBox } from "components";
import { useDBServices } from "hooks/useDBService/useDBService";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type ConsultationsTableItem = Consultation & { actions?: undefined };

export interface ConsultationsTableProps {
  clientId?: string;
  clientName?: string;
  patientId?: string;
  patientName?: string;
}

// started chatgpt opt
export const ConsultationsTable = ({
  clientId,
  patientId,
}: ConsultationsTableProps) => {
  const { t } = useTranslation();

  const [startAfterTime, setStartAfterTime] = useState<number | undefined>(
    undefined
  );
  const [startBeforeTime, setStartBeforeTime] = useState<number | undefined>(
    undefined
  );

  const { getConsultations } =
    useDBServices().consultationsDBService.consultations;

  const [consultations, setConsultations] = useState<
    undefined | null | ConsultationsTableItem[]
  >(undefined);

  const [refreshing, setRefreshing] = useState<boolean>(false);

  const [refresh, setRefresh] = useState(id_util.newId36());

  // console.log(consultations);

  const handleNextPage = () => {
    fetchConsultations({ startAfterTime });
  };

  const handlePreviousPage = () => {
    fetchConsultations({ startBeforeTime });
  };

  interface fetchConsultationsProps {
    startAfterTime?: number;
    startBeforeTime?: number;
    startAtTime?: number;
    showComplete?: boolean;
  }
  const fetchConsultations = useCallback(
    async ({
      startAfterTime,
      startBeforeTime,
      showComplete,
      startAtTime,
    }: fetchConsultationsProps) => {
      setRefreshing(true);
      const props = {
        showComplete,
        startAfterTime,
        startBeforeTime,
        startAtTime,
        clientId,
        patientId,
      };
      const result = await getConsultations(props);

      setRefreshing(false);
      // ... handle pagination ...
      if (result.payload && result.payload.length > 0) {
        setConsultations(result.payload);
        setStartAfterTime(result.payload[result.payload.length - 1].createdAt);
        setStartBeforeTime(result.payload[0].createdAt);
      } else {
        // // No more data to fetch
        if (startAfterTime) setStartAfterTime(undefined);
        if (startBeforeTime) setStartBeforeTime(undefined);
      }
    },
    [clientId, getConsultations, patientId]
  );

  useEffect(() => {
    fetchConsultations({});
  }, [fetchConsultations, refresh]);

  const getRow = useCallback(
    (consultation: Consultation) => {
      const { id, title, patientName, createdAt, status } = consultation;
      return (
        <tr key={id}>
          <td>
            <Text>{tu.getDateAndTimeString(createdAt)}</Text>
          </td>
          <td>
            <Text>{patientName}</Text>
          </td>
          <td>
            <Text>{title}</Text>
          </td>
          <td>
            <Text>{t(status)}</Text>
          </td>
          <td>
            <Anchor
              color="dark"
              weight={400}
              lineClamp={1}
              component={Link}
              to={`/consultations/${id}/summary`}
            >
              {<IconExternalLink size={12} />}
            </Anchor>
          </td>
        </tr>
      );
    },
    [t]
  );

  const rows = useMemo(
    () => (consultations ?? []).map((a) => getRow(a)),
    [consultations, getRow]
  );

  return consultations === null ? (
    <Text>{t("ERROR")}</Text>
  ) : (
    <BBox
      header={
        <Group position="apart">
          <Text>{t("CONSULTATIONS")}</Text>
          <Group>
            <ActionIcon
              onClick={() => {
                setRefresh(id_util.newId36());
              }}
            >
              {refreshing ? <Loader size="xs" /> : <IconRefresh size={18} />}
            </ActionIcon>
          </Group>
        </Group>
      }
    >
      <Box p="xl">
        <Table>
          <thead>
            <tr>
              <th>{t("DATE")}</th>
              <th>{t("PATIENT")}</th>
              <th>{t("TITLE")}</th>
              <th>{t("STATUS")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
        <Group position="right" mt="xl">
          <Button onClick={handlePreviousPage} disabled={!startBeforeTime}>
            {t("PREVIOUS")}
          </Button>
          <Button onClick={handleNextPage} disabled={!startAfterTime}>
            {t("NEXT")}
          </Button>
        </Group>
      </Box>
    </BBox>
  );
};
